
import { supabase } from '@/integrations/supabase/client';
import { User } from '@/types';

export interface StoreUser extends User {
  points: number;
  connectedAccountId: string | null;
  lastOrderDate: string | null;
  totalOrders: number;
  totalSpent: number;
}

export const getStoreUsers = async (storeId: string): Promise<StoreUser[]> => {
  try {
    // Usar query direta em vez de RPC que pode não existir
    const { data, error } = await supabase
      .from('user_stores')
      .select(`
        user_id,
        profiles!user_stores_user_id_fkey(
          id,
          username,
          email,
          avatar_url,
          role,
          status,
          created_at,
          points,
          connected_account_id
        )
      `)
      .eq('store_id', storeId);
    
    if (error) throw error;
    
    return (data || []).map((userStore: any) => {
      const profile = userStore.profiles;
      return {
        id: profile.id,
        name: profile.username || '',
        email: profile.email || '',
        avatar: profile.avatar_url || '',
        role: profile.role as 'admin' | 'user',
        status: profile.status as 'active' | 'inactive',
        createdAt: profile.created_at,
        points: profile.points || 0,
        connectedAccountId: profile.connected_account_id,
        lastOrderDate: null, // To be calculated from orders
        totalOrders: 0, // To be calculated from orders
        totalSpent: 0, // To be calculated from orders
      };
    });
  } catch (error) {
    throw new Error('Não foi possível carregar os usuários da loja');
  }
};

export const getUsersByStoreDetailed = async (storeId: string): Promise<User[]> => {
  try {
    const { data, error } = await supabase
      .from('user_stores')
      .select(`
        user_id,
        profiles!user_stores_user_id_fkey(
          id,
          username,
          email,
          avatar_url,
          role,
          status,
          points,
          connected_account_id,
          created_at
        )
      `)
      .eq('store_id', storeId);
    
    if (error) throw error;
    
    return (data || []).map((userStore: any) => {
      const profile = userStore.profiles;
      return {
        id: profile.id,
        name: profile.username || '',
        username: profile.username || '',
        email: profile.email || '',
        avatar: profile.avatar_url || '',
        avatar_url: profile.avatar_url || '',
        role: profile.role as 'admin' | 'user',
        status: profile.status as 'active' | 'inactive',
        points: profile.points || 0,
        connectedAccountId: profile.connected_account_id,
        createdAt: profile.created_at,
      };
    });
  } catch (error) {
    throw new Error('Não foi possível carregar os usuários da loja');
  }
};

export const getStoreUsersByPoints = async (storeId: string, minPoints: number = 0): Promise<StoreUser[]> => {
  try {
    const { data, error } = await supabase
      .from('user_stores')
      .select(`
        user_id,
        profiles!user_stores_user_id_fkey(
          id,
          username,
          email,
          avatar_url,
          role,
          status,
          created_at,
          points,
          connected_account_id
        )
      `)
      .eq('store_id', storeId)
      .gte('profiles.points', minPoints);
    
    if (error) throw error;
    
    const users = data || [];
    
    if (users.length === 0) {
      return [];
    }
    
    return users.map((userStore: any) => {
      const profile = userStore.profiles;
      return {
        id: profile.id,
        name: profile.username || '',
        email: profile.email || '',
        avatar: profile.avatar_url || '',
        role: profile.role as 'admin' | 'user',
        status: profile.status as 'active' | 'inactive',
        createdAt: profile.created_at,
        points: profile.points || 0,
        connectedAccountId: profile.connected_account_id,
        lastOrderDate: null,
        totalOrders: 0,
        totalSpent: 0,
      };
    });
  } catch (error) {
    throw new Error('Não foi possível carregar os usuários por storepoints');
  }
};

export const getUserStoreAssignments = async (userId: string) => {
  try {
    const { data, error } = await supabase
      .from('user_stores')
      .select(`
        *,
        stores(id, name, location)
      `)
      .eq('user_id', userId);
    
    if (error) throw error;
    
    return data || [];
  } catch (error) {
    throw new Error('Não foi possível carregar as associações de loja');
  }
};

export const assignUserToStore = async (userId: string, storeId: string, isDefault: boolean = false) => {
  try {
    const { data, error } = await supabase
      .from('user_stores')
      .insert({
        user_id: userId,
        store_id: storeId,
        is_default: isDefault
      });
    
    if (error) throw error;
    
    return { success: true };
  } catch (error) {
    return { success: false, error: 'Não foi possível associar o usuário à loja' };
  }
};

export const removeUserFromStore = async (userId: string, storeId: string) => {
  try {
    const { data, error } = await supabase
      .from('user_stores')
      .delete()
      .eq('user_id', userId)
      .eq('store_id', storeId);
    
    if (error) throw error;
    
    return { success: true };
  } catch (error) {
    return { success: false, error: 'Não foi possível remover o usuário da loja' };
  }
};

export const getStoresByUser = async (userId: string) => {
  try {
    const { data, error } = await supabase
      .from('user_stores')
      .select(`
        *,
        stores(*)
      `)
      .eq('user_id', userId);
    
    if (error) throw error;
    
    return (data || []).map(userStore => userStore.stores);
  } catch (error) {
    throw new Error('Não foi possível carregar as lojas do usuário');
  }
};
